/**
 * NodeChip Component
 * Small card displaying a constellation node with type header and weight controls
 */

import type { ConstellationNode } from '../../types/explore.types';

interface NodeChipProps {
  node: ConstellationNode;
  index: number;
  onRemove: (index: number) => void;
  onWeightChange: (index: number, weight: number) => void;
}

/**
 * Color mapping for node types
 */
const NODE_COLORS: Record<string, { bg: string; border: string; text: string; header: string }> = {
  artist: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-900',
    header: 'bg-purple-100 text-purple-800',
  },
  genre: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-900',
    header: 'bg-blue-100 text-blue-800',
  },
  mood: {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-900',
    header: 'bg-pink-100 text-pink-800',
  },
  texture: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-900',
    header: 'bg-orange-100 text-orange-800',
  },
  scene: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-900',
    header: 'bg-green-100 text-green-800',
  },
  label: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-900',
    header: 'bg-yellow-100 text-yellow-800',
  },
  era: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-900',
    header: 'bg-indigo-100 text-indigo-800',
  },
  instrument: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-900',
    header: 'bg-teal-100 text-teal-800',
  },
  characteristic: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-900',
    header: 'bg-gray-100 text-gray-800',
  },
};

/**
 * Icon for node types
 */
const NODE_ICONS: Record<string, string> = {
  artist: '👤',
  genre: '🎵',
  mood: '💭',
  texture: '🎨',
  scene: '🌍',
  label: '🏷️',
  era: '📅',
  instrument: '🎸',
  characteristic: '✨',
};

export default function NodeChip({ node, index, onRemove, onWeightChange }: NodeChipProps) {
  const colors = NODE_COLORS[node.type] || NODE_COLORS.characteristic;
  const icon = NODE_ICONS[node.type] || NODE_ICONS.characteristic;

  const handleIncrement = () => {
    onWeightChange(index, Math.min(1, node.weight + 0.1));
  };

  const handleDecrement = () => {
    onWeightChange(index, Math.max(0, node.weight - 0.1));
  };

  return (
    <div
      className={`${colors.bg} ${colors.border} border-2 rounded-lg overflow-hidden transition-all hover:shadow-md`}
    >
      {/* Header with Type */}
      <div className={`${colors.header} px-3 py-1 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <span className="text-xs font-semibold uppercase tracking-wide">
            {node.type}
          </span>
        </div>
        <button
          onClick={() => onRemove(index)}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-black/10 transition-colors"
          title="Remove node"
        >
          <span className="text-sm">×</span>
        </button>
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        <div className={`font-medium ${colors.text} mb-2 text-sm`}>
          {node.value}
        </div>

        {/* Weight Controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Weight:</span>
          <button
            onClick={handleDecrement}
            disabled={node.weight <= 0}
            className="w-6 h-6 flex items-center justify-center rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm"
            title="Decrease weight"
          >
            −
          </button>
          <span className="text-xs font-mono font-bold w-8 text-center" title="Weight (0-1)">
            {node.weight.toFixed(1)}
          </span>
          <button
            onClick={handleIncrement}
            disabled={node.weight >= 1}
            className="w-6 h-6 flex items-center justify-center rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm"
            title="Increase weight"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}
